import * as Sentry from "@sentry/remix";
import { isRouteErrorResponse } from "@remix-run/react";

let anonymousVisitorId: string | undefined;

// Memory only (no cookie/storage), so guests count as users without being tracked across visits.
export const getAnonymousVisitorId = () => {
  anonymousVisitorId ??= `anon-${Math.random().toString(36).slice(2, 12)}`;
  return anonymousVisitorId;
};

/**
 * Reports an error to Sentry, picking the right capture strategy for its shape.
 *
 * @param error      The caught value of any type (Response, Error, or unknown).
 * @param origin     Where it was caught, added as a tag.
 * @param message    Title template for response cases; `{origin}` and `{error}`
 *                   Ignored for exceptions.
 * @param scopeTags  Extra Sentry tags to merge in (e.g. an issue id for filtering).
 * @param scopeExtra Extra context to attach to the event (ids, correlation id).
 */
export const captureError = (
  error: unknown,
  origin: string,
  message: string = "An {origin} error occurred {error}",
  scopeTags: Record<string, string> = {},
  scopeExtra: Record<string, unknown> = {},
) => {
  const tags = { origin, ...scopeTags };
  const extra = {
    pathname: typeof window !== "undefined" ? window.location.pathname : null,
    ...scopeExtra,
  };

  const buildMessage = (origin: string, error: string) => {
    return message.replace("{origin}", origin).replace("{error}", error);
  };

  if (isRouteErrorResponse(error)) {
    Sentry.captureMessage(
      buildMessage(origin, `${error.status} - ${error.statusText}`),
      {
        tags,
        level: error.status >= 500 ? "error" : "warning",
        extra: {
          ...extra,
          kind: "response",
          status: error.status,
          statusText: error.statusText,
          data: error.data,
        },
      }
    );
    return;
  }

  if (error instanceof Response) {
    Sentry.captureMessage(
      buildMessage(origin, `${error.status} - ${error.statusText}`),
      {
        level: error.status >= 500 ? "error" : "warning",
        tags,
        extra: {
          ...extra,
          kind: "response",
          status: error.status,
          statusText: error.statusText,
        },
    });
    return;
  }

  if (error instanceof Error) {
    Sentry.captureException(error, {
      tags,
      extra: {
        ...extra,
        kind: "error",
        name: error.name,
        message: error.message,
        stack: error.stack,
      },
    });
    return;
  }

  Sentry.captureException(error, {
    tags,
    extra: {
      ...extra,
      kind: "unknown",
      error: String(error),
    },
  });
}
